import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/constants';
import { FaDollarSign, FaChartBar, FaEye, FaCalendarAlt, FaCreditCard, FaCheckCircle, FaClock, FaTimesCircle } from 'react-icons/fa';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './EarningsScreen.css';

const EarningsScreen = () => {
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [lastUpdated, setLastUpdated] = useState('');
  const [transactions, setTransactions] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem('ACCESS_TOKEN');
        
        if (!accessToken) {
          throw new Error('Access token not found. Please log in again.');
        }

        const response = await fetch(`${API_BASE_URL}api/doctor/revenue`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setTotalEarnings(data.totalEarnings || 0);
          
          const formattedTransactions = (data.transactions || []).map((transaction) => ({
            id: transaction._id,
            patientId: transaction.patientId,
            fees: transaction.fees,
            selectedDate: new Date(transaction.selectedDate).toLocaleDateString(),
            paymentMethod: transaction.paymentMethod,
            paymentStatus: transaction.paymentStatus,
            createdAt: new Date(transaction.createdAt).toLocaleDateString(),
          }));
          setTransactions(formattedTransactions);

          const formattedMonthlyEarnings = (data.monthlyEarnings || []).map((earning) => ({
            month: earning.month,
            amount: earning.amount,
          }));
          setEarnings(formattedMonthlyEarnings);

          setLastUpdated(new Date().toLocaleString());
        } else {
          console.error('Error fetching earnings:', await response.text());
        }
      } catch (error) {
        console.error('Failed to fetch earnings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, []);

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <FaCheckCircle className="status-icon completed" />;
      case 'pending':
        return <FaClock className="status-icon pending" />;
      case 'failed':
        return <FaTimesCircle className="status-icon failed" />;
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return 'completed';
      case 'pending':
        return 'pending';
      case 'failed':
        return 'failed';
      default:
        return 'pending';
    }
  };

  if (loading) {
    return (
      <div className="earnings-loading">
        <div className="loading-spinner"></div>
        <p>Loading earnings data...</p>
      </div>
    );
  }

  return (
    <DashboardLayout title="Earnings" showBackButton={true}>
      <div className="earnings-container">
        {/* Header */}
        <div className="earnings-header">
          <h1 className="earnings-title">Earnings Overview</h1>
          <p className="earnings-subtitle">Track your revenue and transaction history</p>
        </div>

        {/* Total Earnings Card */}
        <div className="total-earnings-card">
          <div className="earnings-icon">
            <FaDollarSign />
          </div>
          <div className="earnings-content">
            <h2 className="earnings-amount">₹{totalEarnings.toLocaleString()}</h2>
            <p className="earnings-label">Total Earnings</p>
            <p className="last-updated">Last Updated: {lastUpdated}</p>
          </div>
        </div>

        {/* Monthly Earnings Chart */}
        <div className="chart-section">
          <div className="chart-header">
            <h3 className="chart-title">
              <FaChartBar />
              Monthly Earnings
            </h3>
          </div>
          
          {earnings.length > 0 ? (
            <div className="chart-container">
              <div className="chart-bars">
                {earnings.map((item, index) => {
                  const maxAmount = Math.max(...earnings.map(e => e.amount));
                  const height = maxAmount > 0 ? (item.amount / maxAmount) * 100 : 0;
                  
                  return (
                    <div key={index} className="chart-bar-container">
                      <div className="chart-bar-wrapper">
                        <div 
                          className="chart-bar" 
                          style={{ height: `${height}%` }}
                        >
                          <span className="bar-value">₹{item.amount}</span>
                        </div>
                      </div>
                      <span className="bar-label">{item.month}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="no-data">
              <p>No earnings data available</p>
            </div>
          )}
        </div>

        {/* Transaction History */}
        <div className="transactions-section">
          <div className="transactions-header">
            <h3 className="transactions-title">Recent Transactions</h3>
            <button 
              className="view-all-button"
              onClick={() => navigate('/transaction-history')}
            >
              <FaEye />
              View All
            </button>
          </div>

          {transactions.length > 0 ? (
            <div className="transactions-list">
              {transactions.slice(0, 5).map((transaction) => (
                <div key={transaction.id} className="transaction-item">
                  <div className="transaction-icon">
                    {getStatusIcon(transaction.paymentStatus)}
                  </div>
                  <div className="transaction-details">
                    <div className="transaction-main">
                      <span className="transaction-date">
                        <FaCalendarAlt />
                        {transaction.selectedDate}
                      </span>
                      <span className={`transaction-status ${getStatusClass(transaction.paymentStatus)}`}>
                        {transaction.paymentStatus}
                      </span>
                    </div>
                    <div className="transaction-meta">
                      <span className="payment-method">
                        <FaCreditCard />
                        {transaction.paymentMethod}
                      </span>
                      <span className="created-date">
                        Created: {transaction.createdAt}
                      </span>
                    </div>
                  </div>
                  <div className="transaction-amount">
                    ₹{transaction.fees}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-transactions">
              <p>No transactions available</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default EarningsScreen;